import React, { useEffect, useState, useRef } from 'react';

/**
 * 手环卡片网格布局组件
 * 自适应列数布局，根据屏幕尺寸自动调整
 * 特性：
 * - 自适应列数：最少5列，最多6列，根据容器宽度动态计算
 * - 卡片宽度有上下限：最小180px，最大280px，防止过度拉伸或压缩
 * - 文字不折行，使用省略号处理溢出
 * - 卡片间有固定间距，不重叠
 * - 支持响应式高度调整
 */
const BandCardGrid = ({ 
  children, 
  gap = 12, 
  minCardWidth = 180,
  maxCardWidth = 280,
  className = '' 
}) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // 监听容器宽度变化
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    // 初始更新
    updateWidth();

    // 使用 ResizeObserver 监听容器大小变化
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // 同时监听窗口大小变化（作为备选）
    window.addEventListener('resize', updateWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  // 计算列数 - 根据容器宽度和卡片宽度限制
  const getColumnCount = () => {
    if (!containerWidth) return 5; // 默认5列
    
    // 计算在最小宽度下能放多少列
    const maxColumnsByMinWidth = Math.floor((containerWidth + gap) / (minCardWidth + gap));
    
    // 计算在最大宽度下至少需要多少列
    const minColumnsByMaxWidth = Math.ceil((containerWidth + gap) / (maxCardWidth + gap));
    
    // 列数范围：5-6列
    // 优先满足最少5列，如果空间足够则展示6列
    if (maxColumnsByMinWidth >= 6) {
      return 6;
    }
    return Math.max(5, Math.min(6, maxColumnsByMinWidth));
  };

  // 计算卡片宽度 - 根据列数动态计算
  const getCardStyle = () => {
    const columnCount = getColumnCount();
    
    // 总间距 = gap * (columnCount - 1)
    const totalGap = gap * (columnCount - 1);
    
    // 计算每个卡片的宽度
    const cardWidth = `calc((100% - ${totalGap}px) / ${columnCount})`;
    
    return {
      width: cardWidth,
      minWidth: `${minCardWidth}px`,
      maxWidth: `${maxCardWidth}px`,
      flex: '0 0 auto',
    };
  };

  // 为每个子元素添加样式
  const childrenWithProps = React.Children.map(children, (child) => {
    if (!child) return null;
    
    return React.cloneElement(child, {
      style: {
        ...child.props.style,
        ...getCardStyle(),
        marginBottom: `${gap}px`,
      },
    });
  });

  return (
    <div
      ref={containerRef}
      className={`band-card-grid ${className}`}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: `${gap}px`,
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        boxSizing: 'border-box',
      }}
    >
      {childrenWithProps}
    </div>
  );
};

export default BandCardGrid;
